// src/components/pages/message/timecapsule/VideoRecorderModal.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  LinearProgress,
  Slider,
} from "@mui/material";

const DEFAULT_MAX_SECONDS = 10;

// We allow a grace window so the user reliably gets the full maxSeconds.
// This prevents “cutoff at 9.9s” due to device/recorder timing drift.
const STOP_GRACE_MS = 1000;

function pickBestMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;

  const iosCandidates = [
    "video/mp4;codecs=h264,aac",
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/mp4",
  ];
  const defaultCandidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  const candidates = isIOS ? [...iosCandidates, ...defaultCandidates] : defaultCandidates;

  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return null;
}

const VideoRecorderModal = ({
  open,
  onClose,
  onRecorded, // called only when user taps "Add Video"
  maxSeconds = DEFAULT_MAX_SECONDS,
  withAudio = true,
}) => {
  const liveVideoRef = useRef(null);
  const previewVideoRef = useRef(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const stopTimerRef = useRef(null);
  const tickTimerRef = useRef(null);

  const previewUrlRef = useRef(null);

  const [facingMode, setFacingMode] = useState("environment");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [notSupported, setNotSupported] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState("");
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
  const [currentZoom, setCurrentZoom] = useState(1);

  const isMobile = useMemo(() => /Mobi|Android/i.test(navigator.userAgent), []);
  const isIOS = useMemo(
    () => /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream,
    []
  );
  const isAndroid = useMemo(() => /Android/i.test(navigator.userAgent), []);

  const maxMs = Math.max(1, Number(maxSeconds) || DEFAULT_MAX_SECONDS) * 1000;
  const stopAtMs = maxMs + STOP_GRACE_MS;

  const progress = Math.min(100, Math.round((Math.min(elapsedMs, maxMs) / maxMs) * 100));
  const secondsLeft = Math.max(0, Math.ceil((maxMs - elapsedMs) / 1000));
  const isFinishing = isRecording && elapsedMs >= maxMs;

  const clampZoom = useCallback((value) => {
    const min = Number.isFinite(zoomRange.min) ? zoomRange.min : 1;
    const max = Number.isFinite(zoomRange.max) ? zoomRange.max : min;
    return Math.min(max, Math.max(min, value));
  }, [zoomRange.max, zoomRange.min]);

  const stopTimers = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  const cleanupPreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = null;
    }
  }, []);

  const applyZoom = useCallback(async (nextZoom) => {
    const stream = streamRef.current;
    const track = stream?.getVideoTracks?.()[0];
    if (!track || !zoomSupported) return;
    const clamped = clampZoom(nextZoom);
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped }] });
      setCurrentZoom(clamped);
    } catch (err) {
      console.warn("Failed to apply video camera zoom:", err);
    }
  }, [clampZoom, zoomSupported]);

  const resetAll = useCallback(() => {
    stopTimers();
    setIsRecording(false);
    setElapsedMs(0);

    chunksRef.current = [];
    recorderRef.current = null;

    cleanupPreviewUrl();
    setRecordedBlob(null);
    setRecordedUrl("");
  }, [cleanupPreviewUrl, stopTimers]);

  useEffect(() => {
    if (!open) return;

    setPermissionDenied(false);
    setNotSupported(false);

    if (typeof MediaRecorder === "undefined") {
      setNotSupported(true);
      return;
    }

    const checkPermissions = async () => {
      try {
        if (!navigator.permissions?.query) return;

        const camStatus = await navigator.permissions.query({ name: "camera" });
        const micStatus = withAudio
          ? await navigator.permissions.query({ name: "microphone" })
          : null;

        const denied =
          camStatus.state === "denied" ||
          (micStatus && micStatus.state === "denied");

        setPermissionDenied(denied);

        camStatus.onchange = () => {
          setPermissionDenied(
            camStatus.state === "denied" ||
              (micStatus && micStatus.state === "denied")
          );
        };

        if (micStatus) {
          micStatus.onchange = () => {
            setPermissionDenied(
              camStatus.state === "denied" || micStatus.state === "denied"
            );
          };
        }
      } catch (err) {
        console.warn("Permission check failed:", err);
      }
    };

    checkPermissions();
  }, [open, withAudio]);

  const startPreview = useCallback(async () => {
    try {
      setPermissionDenied(false);
      setZoomSupported(false);
      setZoomRange({ min: 1, max: 1, step: 0.1 });
      setCurrentZoom(1);

      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: !!withAudio,
      });

      streamRef.current = stream;
      const videoTrack = stream?.getVideoTracks?.()[0];
      if (videoTrack) {
        const capabilities = videoTrack.getCapabilities?.() || {};
        const settings = videoTrack.getSettings?.() || {};
        const min = Number.isFinite(capabilities.zoom?.min) ? capabilities.zoom.min : 1;
        const max = Number.isFinite(capabilities.zoom?.max) ? capabilities.zoom.max : min;
        const step = Number.isFinite(capabilities.zoom?.step) ? capabilities.zoom.step : 0.1;
        const supportsZoomRange = max > min;
        const initial = Number.isFinite(settings.zoom) ? settings.zoom : min;
        const normalizedInitial = Math.min(max, Math.max(min, supportsZoomRange ? min : initial));

        setZoomSupported(supportsZoomRange);
        setZoomRange({ min, max, step });
        setCurrentZoom(normalizedInitial);
        if (supportsZoomRange) {
          videoTrack
            .applyConstraints({ advanced: [{ zoom: normalizedInitial }] })
            .catch((err) => {
              console.warn("Failed to apply initial video camera zoom:", err);
            });
        }
      }

      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        await liveVideoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.warn("getUserMedia failed:", err);
      setPermissionDenied(true);
      stopStream();
    }
  }, [facingMode, withAudio, stopStream]);

  const handleZoomSliderChange = useCallback((_, value) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (!Number.isFinite(next)) return;
    applyZoom(next);
  }, [applyZoom]);

  useEffect(() => {
    if (!open) {
      resetAll();
      stopStream();
      return;
    }

    if (!permissionDenied && !notSupported) {
      startPreview();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, permissionDenied, notSupported, startPreview]);

  const stopRecording = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state !== "inactive") r.stop();
  }, []);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || notSupported) return;

    cleanupPreviewUrl();
    setRecordedBlob(null);
    setRecordedUrl("");
    chunksRef.current = [];

    const mimeType = pickBestMimeType();

    let recorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      console.warn("MediaRecorder init failed:", e);
      setNotSupported(true);
      return;
    }

    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onerror = (e) => {
      console.warn("Recorder error:", e);
      stopTimers();
      setIsRecording(false);
      setElapsedMs(0);
    };

    recorder.onstop = () => {
      stopTimers();
      setIsRecording(false);

      const chunks = chunksRef.current;
      if (!chunks.length) return;

      const blob = new Blob(chunks, { type: recorder.mimeType || "video/webm" });
      const url = URL.createObjectURL(blob);

      previewUrlRef.current = url;
      setRecordedBlob(blob);
      setRecordedUrl(url);

      stopStream();
    };

    recorder.start(200);
    setIsRecording(true);

    const startedAt = Date.now();
    tickTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 100);

    // Graceful stop: give a buffer so the user reliably gets the full maxSeconds.
    stopTimerRef.current = setTimeout(() => {
      stopRecording();
    }, stopAtMs);
  }, [cleanupPreviewUrl, notSupported, stopAtMs, stopRecording, stopStream, stopTimers]);

  const handleRetake = useCallback(async () => {
    stopRecording();
    stopTimers();

    cleanupPreviewUrl();
    setRecordedBlob(null);
    setRecordedUrl("");
    setIsRecording(false);
    setElapsedMs(0);
    chunksRef.current = [];

    await startPreview();
  }, [cleanupPreviewUrl, startPreview, stopRecording, stopTimers]);

  const toggleFacingMode = useCallback(() => {
    if (isRecording || recordedUrl) return;
    setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
  }, [isRecording, recordedUrl]);

  const handleAddVideo = useCallback(() => {
    if (!recordedBlob || !recordedUrl) return;

    onRecorded?.({
      blob: recordedBlob,
      url: recordedUrl,
      mimeType: recordedBlob.type || "video/webm",
      // Report the policy duration cap, even though we allowed a grace window
      durationSeconds: Math.min(maxMs, elapsedMs) / 1000,
    });
  }, [elapsedMs, maxMs, onRecorded, recordedBlob, recordedUrl]);

  const handleClose = useCallback(() => {
    stopRecording();
    resetAll();
    stopStream();
    onClose?.();
  }, [onClose, resetAll, stopRecording, stopStream]);

  useEffect(() => {
    if (!recordedUrl) return;
    if (previewVideoRef.current) {
      previewVideoRef.current.load();
      previewVideoRef.current.play?.().catch(() => {});
    }
  }, [recordedUrl]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          p: 2,
          width: "90%",
          maxWidth: 500,
          borderRadius: 1,
        }}
      >
        {notSupported ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              Video recording is not supported in this browser.
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Try Chrome on Android, or desktop Chrome/Edge.
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
            >
              Close
            </Button>
          </Box>
        ) : permissionDenied ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              Camera{withAudio ? " and microphone" : ""} access is blocked. Please
              allow permission in your browser settings.
            </Alert>

            <Typography variant="body2" fontWeight="bold" mb={1}>
              How to enable:
            </Typography>

            {isIOS && (
              <Typography variant="body2" gutterBottom>
                📱 <strong>iOS Safari:</strong> Settings → Safari → Camera{" "}
                {withAudio ? "/ Microphone" : ""} → Allow
              </Typography>
            )}

            {isAndroid && (
              <Typography variant="body2" gutterBottom>
                📱 <strong>Android Chrome:</strong> Chrome (⋮) → Settings → Site
                settings → Camera {withAudio ? "/ Microphone" : ""} → Allow
              </Typography>
            )}

            {!isIOS && !isAndroid && (
              <Typography variant="body2" gutterBottom>
                ⚙️ <strong>Browser:</strong> Site permissions → Camera{" "}
                {withAudio ? "/ Microphone" : ""} → Allow
              </Typography>
            )}

            <Button
              onClick={handleClose}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Record Video
            </Typography>

            {!recordedUrl ? (
              <Box sx={{ width: "100%", borderRadius: 1, overflow: "hidden", position: "relative" }}>
                <video
                  ref={liveVideoRef}
                  muted
                  playsInline
                  style={{ width: "100%", display: "block" }}
                />
                {zoomSupported && (
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      top: 12,
                      right: 10,
                      bottom: 12,
                      width: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 999,
                      backgroundColor: "rgba(0,0,0,0.35)",
                      backdropFilter: "blur(2px)",
                      WebkitBackdropFilter: "blur(2px)",
                      zIndex: 2,
                      "& .MuiSlider-root": {
                        height: "calc(100% - 20px)",
                        color: theme.colors.white,
                      },
                      "& .MuiSlider-thumb": {
                        width: 14,
                        height: 14,
                      },
                      "& .MuiSlider-rail": {
                        opacity: 0.4,
                      },
                    })}
                  >
                    <Slider
                      orientation="vertical"
                      min={zoomRange.min}
                      max={zoomRange.max}
                      step={zoomRange.step || 0.1}
                      value={currentZoom}
                      onChange={handleZoomSliderChange}
                      aria-label="Video camera zoom"
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ width: "100%", borderRadius: 1, overflow: "hidden" }}>
                <video
                  ref={previewVideoRef}
                  playsInline
                  controls
                  style={{ width: "100%", display: "block" }}
                >
                  <source src={recordedUrl} type={recordedBlob?.type || "video/webm"} />
                </video>
              </Box>
            )}

            <Box sx={{ mt: 1 }}>
              {!recordedUrl ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {isRecording
                      ? (isFinishing ? "Recording… finishing" : `Recording… ${secondsLeft}s left`)
                      : `Max length: ${Math.round(maxMs / 1000)}s`}
                  </Typography>
                  <LinearProgress variant="determinate" value={progress} />
                </>
              ) : (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Preview your video. Retake if needed, or add it to your capsule.
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {isMobile && !recordedUrl && (
                <Button
                  onClick={toggleFacingMode}
                  variant="outlined"
                  disabled={isRecording}
                  sx={{
                    fontWeight: 700,
                    px: 3,
                    py: 1.25,
                  }}
                >
                  Switch Camera
                </Button>
              )}

              {!recordedUrl ? (
                !isRecording ? (
                  <Button
                    onClick={startRecording}
                    variant="contained"
                    sx={{
                      fontWeight: 700,
                      px: 3,
                      py: 1.25,
                    }}
                  >
                    Start Recording
                  </Button>
                ) : (
                  <Button
                    onClick={stopRecording}
                    variant="contained"
                    sx={{
                      fontWeight: 700,
                      px: 3,
                      py: 1.25,
                    }}
                  >
                    Stop
                  </Button>
                )
              ) : (
                <>
                  <Button
                    onClick={handleRetake}
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      px: 3,
                      py: 1.25,
                    }}
                  >
                    Retake
                  </Button>
                  <Button
                    onClick={handleAddVideo}
                    variant="contained"
                    sx={{
                      fontWeight: 700,
                      px: 3,
                      py: 1.25,
                    }}
                  >
                    Add Video
                  </Button>
                </>
              )}

              <Button
                onClick={handleClose}
                variant="text"
                sx={{
                  fontWeight: 700,
                  px: 3,
                  py: 1.25,
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default VideoRecorderModal;
